import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import apiUrl from "../Apiurl";
import stilePulsanti from "../interfaccia/stilePulsanti";
import RitentaErroriImmagini from "./RitentaErroriImmagini";

const STATUSES = [
  "",
  "PENDING",
  "IN_PROGRESS",
  "READY_FOR_REVIEW",
  "APPROVED",
  "SUCCESS",
  "ERROR",
  "REJECTED",
];

const STALE_STATUSES = new Set([
  "PENDING",
  "IN_PROGRESS",
  "READY_FOR_REVIEW",
  "APPROVED",
]);
const BULK_IMPORT_WAITING_STEP = "Import massivo schedulato fuori orario";

function authConfig(token) {
  return {
    headers: {
      Authorization: "Bearer " + token,
    },
  };
}

function formatDate(value) {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return date.toLocaleString("it-IT");
}

function formatInteger(value) {
  if (value === null || typeof value === "undefined" || value === "") return "-";
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return "-";
  return Math.round(parsed).toLocaleString("it-IT");
}

function formatPrestashopDuration(job) {
  const hasDuration =
    job?.prestashopDurationMs !== null &&
    typeof job?.prestashopDurationMs !== "undefined" &&
    job?.prestashopDurationMs !== "";
  const hasDelay =
    job?.prestashopForcedDelayMs !== null &&
    typeof job?.prestashopForcedDelayMs !== "undefined" &&
    job?.prestashopForcedDelayMs !== "";
  const duration = hasDuration ? Number(job.prestashopDurationMs) : NaN;
  const delay = hasDelay ? Number(job.prestashopForcedDelayMs) : NaN;
  const durationLabel = Number.isFinite(duration)
    ? `${Math.round(duration).toLocaleString("it-IT")} ms`
    : "-";
  if (!Number.isFinite(delay)) {
    return durationLabel;
  }
  return `${durationLabel} (${Math.round(delay).toLocaleString("it-IT")} ms delay)`;
}

function statusClass(status) {
  if (status === "SUCCESS") return "dark-green";
  if (status === "ERROR" || status === "REJECTED") return "dark-red";
  if (status === "IN_PROGRESS" || status === "APPROVED") return "orange";
  return "gray";
}

function DashboardImmaginiJobsPage({ token }) {
  const [jobs, setJobs] = useState([]);
  const [summary, setSummary] = useState({});
  const [runtimeQueue, setRuntimeQueue] = useState({ pendingOrRunning: 0 });
  const [status, setStatus] = useState("");
  const [productCode, setProductCode] = useState("");
  const [uploadedBy, setUploadedBy] = useState("");
  const [limit, setLimit] = useState(100);
  const [offset, setOffset] = useState(0);
  const [staleOnly, setStaleOnly] = useState(false);
  const [staleMinutes, setStaleMinutes] = useState(10);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [loading, setLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState("");
  const [actionMessage, setActionMessage] = useState("");
  const [detailLoading, setDetailLoading] = useState("");
  const [detailJob, setDetailJob] = useState(null);
  const [temboDebugLoading, setTemboDebugLoading] = useState(false);
  const [temboDebugResponse, setTemboDebugResponse] = useState(null);
  const [error, setError] = useState("");

  const totalSummary = useMemo(
    () =>
      Object.values(summary).reduce((total, value) => {
        const parsed = Number(value ?? 0);
        return total + (Number.isFinite(parsed) ? parsed : 0);
      }, 0),
    [summary],
  );

  const fetchJobs = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    setError("");
    try {
      const response = await axios.get(`${apiUrl}/moda/image-jobs`, {
        ...authConfig(token),
        params: {
          status: status || undefined,
          productCode: productCode.trim() || undefined,
          uploadedBy: uploadedBy.trim() || undefined,
          staleMinutes: staleOnly ? staleMinutes : undefined,
          limit,
          offset,
        },
      });
      setJobs(response.data?.data ?? []);
      setSummary(response.data?.meta?.summary ?? {});
      setRuntimeQueue(
        response.data?.meta?.runtimeQueue ?? { pendingOrRunning: 0 },
      );
    } catch (requestError) {
      setError(
        requestError.response?.data?.error ||
          requestError.message ||
          "Errore caricamento job immagini.",
      );
    } finally {
      setLoading(false);
    }
  }, [
    limit,
    offset,
    productCode,
    staleMinutes,
    staleOnly,
    status,
    token,
    uploadedBy,
  ]);

  useEffect(() => {
    fetchJobs();
  }, [fetchJobs]);

  useEffect(() => {
    if (!autoRefresh) return undefined;
    const intervalId = setInterval(fetchJobs, 5000);
    return () => clearInterval(intervalId);
  }, [autoRefresh, fetchJobs]);

  const resetFilters = () => {
    setStatus("");
    setProductCode("");
    setUploadedBy("");
    setStaleOnly(false);
    setOffset(0);
  };

  const retryApprovedStale = async () => {
    if (!token || actionLoading) return;
    setActionLoading("retry");
    setActionMessage("");
    setError("");
    try {
      const response = await axios.post(
        `${apiUrl}/moda/image-jobs/retry-approved-stale`,
        {
          staleMinutes,
          limit: 200,
        },
        authConfig(token),
      );
      const data = response.data?.data ?? {};
      setActionMessage(
        `Retry schedulato: ${Number(data.scheduled ?? 0).toLocaleString(
          "it-IT",
        )} job, saltati ${Number(data.skipped ?? 0).toLocaleString("it-IT")}.`,
      );
      await fetchJobs();
    } catch (requestError) {
      setError(
        requestError.response?.data?.error ||
          requestError.message ||
          "Errore retry job APPROVED.",
      );
    } finally {
      setActionLoading("");
    }
  };

  const retryReadyAutoApproveStale = async () => {
    if (!token || actionLoading) return;
    setActionLoading("retryReady");
    setActionMessage("");
    setError("");
    try {
      const response = await axios.post(
        `${apiUrl}/moda/image-jobs/retry-ready-autoapprove-stale`,
        {
          staleMinutes,
          limit: 200,
        },
        authConfig(token),
      );
      const data = response.data?.data ?? {};
      setActionMessage(
        `Retry READY schedulato: ${Number(data.scheduled ?? 0).toLocaleString(
          "it-IT",
        )} job, saltati ${Number(data.skipped ?? 0).toLocaleString("it-IT")}.`,
      );
      await fetchJobs();
    } catch (requestError) {
      setError(
        requestError.response?.data?.error ||
          requestError.message ||
          "Errore retry job READY_FOR_REVIEW.",
      );
    } finally {
      setActionLoading("");
    }
  };

  const markStaleError = async () => {
    if (!token || actionLoading) return;
    const confirmed = window.confirm(
      `Marcare come ERROR i job attivi fermi da almeno ${staleMinutes} minuti?`,
    );
    if (!confirmed) return;

    setActionLoading("error");
    setActionMessage("");
    setError("");
    try {
      const response = await axios.post(
        `${apiUrl}/moda/image-jobs/mark-stale-error`,
        {
          staleMinutes,
          productCode: productCode.trim() || undefined,
          uploadedBy: uploadedBy.trim() || undefined,
        },
        authConfig(token),
      );
      setActionMessage(
        `Job marcati ERROR: ${Number(
          response.data?.data?.updated ?? 0,
        ).toLocaleString("it-IT")}.`,
      );
      await fetchJobs();
    } catch (requestError) {
      setError(
        requestError.response?.data?.error ||
          requestError.message ||
          "Errore marcatura job in stallo.",
      );
    } finally {
      setActionLoading("");
    }
  };

  const loadJobDetail = async (job) => {
    if (!token || !job?.jobId || !job?.productCode) return;
    setDetailLoading(job.jobId);
    setError("");
    try {
      const response = await axios.get(
        `${apiUrl}/moda/articoli/${encodeURIComponent(
          job.productCode,
        )}/immagini/${encodeURIComponent(job.jobId)}`,
        authConfig(token),
      );
      setDetailJob(response.data?.job ?? null);
      setTemboDebugResponse(null);
    } catch (requestError) {
      setError(
        requestError.response?.data?.error ||
          requestError.message ||
          "Errore caricamento dettaglio job.",
      );
    } finally {
      setDetailLoading("");
    }
  };

  const debugTemboResponse = async (site) => {
    if (!token || !detailJob?.jobId || temboDebugLoading) return;
    const confirmed = window.confirm(
      `Richiamare ora l'endpoint Tembo images/updateSync ${site} per questo job? La chiamata puo' avere effetti su Prestashop e non e' una verifica in sola lettura.`,
    );
    if (!confirmed) return;

    setTemboDebugLoading(true);
    setError("");
    setTemboDebugResponse(null);
    try {
      const response = await axios.post(
        `${apiUrl}/moda/image-jobs/${encodeURIComponent(
          detailJob.jobId,
        )}/debug-prestashop-response`,
        { site },
        authConfig(token),
      );
      setTemboDebugResponse(response.data?.data ?? null);
    } catch (requestError) {
      setError(
        requestError.response?.data?.error ||
          requestError.message ||
          "Errore debug risposta Tembo.",
      );
    } finally {
      setTemboDebugLoading(false);
    }
  };

  const isJobStale = useCallback(
    (job) => {
      if (!STALE_STATUSES.has(job.status) || !job.updatedAt) return false;
      if (job.lastStep === BULK_IMPORT_WAITING_STEP) return false;
      const updatedAt = new Date(job.updatedAt).getTime();
      if (Number.isNaN(updatedAt)) return false;
      return Date.now() - updatedAt > staleMinutes * 60 * 1000;
    },
    [staleMinutes],
  );

  return (
    <div className="pa3">
      <div className="flex flex-wrap items-center justify-between mb3">
        <h2 className="ma0">Dashboard immagini</h2>
        <div className="flex items-center">
          <label className="mr3 f6">
            <input
              type="checkbox"
              className="mr1"
              checked={autoRefresh}
              onChange={(event) => setAutoRefresh(event.target.checked)}
            />
            Auto refresh
          </label>
          <button
            className={stilePulsanti.buttonClassesWhite}
            onClick={fetchJobs}
            disabled={loading}
          >
            Aggiorna
          </button>
        </div>
      </div>

      <div className="flex flex-wrap nl2 nr2 mb3">
        {["PENDING", "IN_PROGRESS", "APPROVED", "SUCCESS", "ERROR"].map(
          (item) => (
            <div key={item} className="pa2 w-50 w-20-l">
              <button
                type="button"
                className="w-100 tl ba b--black-10 pa3 bg-white pointer"
                onClick={() => {
                  setStatus(item);
                  setOffset(0);
                }}
              >
                <div className="f7 ttu tracked gray">{item}</div>
                <div className={`f3 fw6 ${statusClass(item)}`}>
                  {Number(summary[item] ?? 0).toLocaleString("it-IT")}
                </div>
              </button>
            </div>
          ),
        )}
      </div>

      <div className="mb3 pa3 ba b--black-10 bg-near-white">
        <div className="flex flex-wrap items-end nl2 nr2">
          <label className="pa2 w-100 w-20-ns">
            <span className="db f7 ttu tracked gray mb1">Stato</span>
            <select
              className="pa2 input-reset ba b--black-20 w-100"
              value={status}
              onChange={(event) => {
                setStatus(event.target.value);
                setOffset(0);
              }}
            >
              {STATUSES.map((item) => (
                <option key={item || "ALL"} value={item}>
                  {item || "Tutti"}
                </option>
              ))}
            </select>
          </label>
          <label className="pa2 w-100 w-20-ns">
            <span className="db f7 ttu tracked gray mb1">codVS</span>
            <input
              className="pa2 input-reset ba b--black-20 w-100"
              value={productCode}
              onChange={(event) => {
                setProductCode(event.target.value);
                setOffset(0);
              }}
              placeholder="67505"
            />
          </label>
          <label className="pa2 w-100 w-30-ns">
            <span className="db f7 ttu tracked gray mb1">Utente</span>
            <input
              className="pa2 input-reset ba b--black-20 w-100"
              value={uploadedBy}
              onChange={(event) => {
                setUploadedBy(event.target.value);
                setOffset(0);
              }}
              placeholder="email"
            />
          </label>
          <label className="pa2 w-100 w-20-ns">
            <span className="db f7 ttu tracked gray mb1">Stallo</span>
            <span className="db pa2 input-reset ba b--black-20 bg-white">
              <input
                type="checkbox"
                className="mr1"
                checked={staleOnly}
                onChange={(event) => {
                  setStaleOnly(event.target.checked);
                  setOffset(0);
                }}
              />
              Solo fermi da almeno
            </span>
          </label>
          <label className="pa2 w-100 w-10-ns">
            <span className="db f7 ttu tracked gray mb1">Minuti</span>
            <input
              className="pa2 input-reset ba b--black-20 w-100"
              type="number"
              min="1"
              max="1440"
              value={staleMinutes}
              onChange={(event) => {
                const parsed = Number(event.target.value);
                setStaleMinutes(Number.isFinite(parsed) ? parsed : 10);
                setOffset(0);
              }}
            />
          </label>
          <label className="pa2 w-100 w-10-ns">
            <span className="db f7 ttu tracked gray mb1">Righe</span>
            <select
              className="pa2 input-reset ba b--black-20 w-100"
              value={limit}
              onChange={(event) => {
                setLimit(Number(event.target.value));
                setOffset(0);
              }}
            >
              {[50, 100, 200, 500].map((value) => (
                <option key={value} value={value}>
                  {value}
                </option>
              ))}
            </select>
          </label>
          <div className="pa2">
            <button className={stilePulsanti.buttonClassesWhite} onClick={resetFilters}>
              Pulisci
            </button>
          </div>
          <div className="pa2">
            <button
              className={stilePulsanti.buttonClassesGreen}
              onClick={retryReadyAutoApproveStale}
              disabled={Boolean(actionLoading)}
            >
              Riprova READY
            </button>
          </div>
          <div className="pa2">
            <button
              className={stilePulsanti.buttonClassesGreen}
              onClick={retryApprovedStale}
              disabled={Boolean(actionLoading)}
            >
              Riprova APPROVED
            </button>
          </div>
          <div className="pa2">
            <button
              className={stilePulsanti.buttonClassesRed}
              onClick={markStaleError}
              disabled={Boolean(actionLoading)}
            >
              Marca stallo ERROR
            </button>
          </div>
          <RitentaErroriImmagini
            token={token}
            onCompletato={fetchJobs}
            disabled={Boolean(actionLoading)}
          />
        </div>
      </div>

      {loading && <div className="pa2 colPreparazione">Caricamento job...</div>}
      {error && <div className="pa2 dark-red">{error}</div>}
      {actionMessage && <div className="pa2 dark-green">{actionMessage}</div>}
      {detailJob && (
        <div className="mb3 pa3 ba b--black-10 bg-white">
          <div className="flex items-center justify-between mb2">
            <div className="f6 fw6">
              Dettaglio job {detailJob.productCode} - {detailJob.status}
            </div>
            <div>
              <button
                className={stilePulsanti.buttonClassesOrange}
                onClick={() => debugTemboResponse("VIG")}
                disabled={temboDebugLoading}
              >
                Debug Tembo VIG
              </button>
              <button
                className={stilePulsanti.buttonClassesOrange}
                onClick={() => debugTemboResponse("BOT")}
                disabled={temboDebugLoading}
              >
                Debug Tembo BOT
              </button>
              <button
                className={stilePulsanti.buttonClassesWhite}
                onClick={() => {
                  setDetailJob(null);
                  setTemboDebugResponse(null);
                }}
              >
                Chiudi
              </button>
            </div>
          </div>
          {temboDebugResponse && (
            <div className="mb3">
              <div className="f7 ttu tracked gray mb1">Risposta Tembo</div>
              <pre className="pa3 bg-near-white overflow-auto f7">
                {JSON.stringify(temboDebugResponse, null, 2)}
              </pre>
            </div>
          )}
          <div className="mb3 flex flex-wrap nl2 nr2">
            <div className="pa2">
              <div className="f7 ttu tracked gray">Tentativi Prestashop</div>
              <div className="fw6">{formatInteger(detailJob.prestashopAttempts)}</div>
            </div>
            <div className="pa2">
              <div className="f7 ttu tracked gray">Tembo ms (delay)</div>
              <div className="fw6">{formatPrestashopDuration(detailJob)}</div>
            </div>
          </div>
          <pre className="pa3 bg-near-white overflow-auto f7">
            {JSON.stringify(detailJob.metadata ?? detailJob, null, 2)}
          </pre>
        </div>
      )}

      <div className="mb2 f6 gray">
        Riepilogo filtri: {totalSummary.toLocaleString("it-IT")} job. Coda API
        viva:{" "}
        {Number(runtimeQueue.pendingOrRunning ?? 0).toLocaleString("it-IT")} job.
        Vista da {offset + 1} a {offset + jobs.length}.
      </div>

      <div className="overflow-auto">
        <table className="w-100 collapse f6 bg-white">
          <thead>
            <tr className="tl bb b--black-10">
              <th className="pv2 pr2">Stato</th>
              <th className="pv2 pr2">codVS</th>
              <th className="pv2 pr2">Brand</th>
              <th className="pv2 pr2">File</th>
              <th className="pv2 pr2">Step</th>
              <th className="pv2 pr2">Tent.</th>
              <th className="pv2 pr2">Tembo ms (delay)</th>
              <th className="pv2 pr2">Messaggio</th>
              <th className="pv2 pr2">Stallo</th>
              <th className="pv2 pr2">Aggiornato</th>
              <th className="pv2 pr2">Utente</th>
              <th className="pv2 pr2">Dettaglio</th>
              <th className="pv2 pr2">jobId</th>
            </tr>
          </thead>
          <tbody>
            {jobs.map((job) => (
              <tr key={job.jobId} className="bb b--black-05">
                <td className={`pv2 pr2 fw6 ${statusClass(job.status)}`}>
                  {job.status}
                </td>
                <td className="pv2 pr2">{job.productCode}</td>
                <td className="pv2 pr2">{job.brandCode || "-"}</td>
                <td className="pv2 pr2">
                  {job.processedFiles}/{job.totalFiles}
                </td>
                <td className="pv2 pr2">{job.lastStep || "-"}</td>
                <td className="pv2 pr2">{formatInteger(job.prestashopAttempts)}</td>
                <td className="pv2 pr2">{formatPrestashopDuration(job)}</td>
                <td className="pv2 pr2 mw6">{job.message || "-"}</td>
                <td className={`pv2 pr2 fw6 ${isJobStale(job) ? "dark-red" : "gray"}`}>
                  {isJobStale(job) ? "SI" : "-"}
                </td>
                <td className="pv2 pr2">{formatDate(job.updatedAt)}</td>
                <td className="pv2 pr2">{job.uploadedBy || "-"}</td>
                <td className="pv2 pr2">
                  <button
                    className={stilePulsanti.buttonClassesWhite}
                    onClick={() => loadJobDetail(job)}
                    disabled={detailLoading === job.jobId}
                  >
                    Dettaglio
                  </button>
                </td>
                <td className="pv2 pr2">{job.jobId}</td>
              </tr>
            ))}
            {!jobs.length && !loading && (
              <tr>
                <td className="pv3 tc gray" colSpan="13">
                  Nessun job immagini trovato.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="flex justify-between items-center mt3">
        <button
          className={stilePulsanti.buttonClassesWhite}
          onClick={() => setOffset(Math.max(0, offset - limit))}
          disabled={offset === 0 || loading}
        >
          Precedenti
        </button>
        <button
          className={stilePulsanti.buttonClassesWhite}
          onClick={() => setOffset(offset + limit)}
          disabled={jobs.length < limit || loading}
        >
          Successivi
        </button>
      </div>
    </div>
  );
}

export default DashboardImmaginiJobsPage;
